import React, { useState, useEffect } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import InteractiveQuiz from './InteractiveQuiz.jsx';
import './QuizEmbed.css';
import { useExerciseSync } from '../../context/ExerciseSyncContext';

const QuizEmbed = ({ node, updateAttributes, editor, getPos }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const exerciseIdRef = React.useRef(node.attrs.exerciseId);
  if (!exerciseIdRef.current) exerciseIdRef.current = crypto.randomUUID();
  const exerciseId = exerciseIdRef.current;
  useEffect(() => {
    if (!node.attrs.exerciseId) updateAttributes({ exerciseId });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { myState, otherState, updateState, liveUpdateCount } = useExerciseSync(exerciseId);

  // Force ProseMirror to re-measure after expand/collapse so click coordinates stay accurate
  useEffect(() => {
    if (!editor) return;
    const id = requestAnimationFrame(() => {
      try {
        editor.view.updateState(editor.view.state);
        // Mobile Safari/Chrome: re-syncing the view can leave the browser
        // thinking the (still logically-selected) editable region wants the
        // on-screen keyboard, even though the user only tapped this
        // exercise's own contentEditable=false UI. Blur ONLY if the outer
        // editor root itself still has focus (not an input inside the
        // exercise's own edit mode), so the keyboard does not pop on
        // expand/collapse.
        if (typeof document !== 'undefined' && document.activeElement === editor.view.dom) {
          editor.view.dom.blur();
        }
      } catch (_) {}
    });
    return () => cancelAnimationFrame(id);
  }, [isExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mirror a REMOTE peer's expand/collapse live, without replaying stale
  // localStorage-hydrated data on every remount — see ExerciseSyncContext's
  // liveUpdateCount for why this can't just watch otherState.expanded alone.
  const lastLiveUpdateCountRef = React.useRef(liveUpdateCount);
  useEffect(() => {
    if (liveUpdateCount === lastLiveUpdateCountRef.current) return;
    lastLiveUpdateCountRef.current = liveUpdateCount;
    if (otherState.expanded !== undefined && otherState.expanded !== isExpanded) {
      setIsExpanded(otherState.expanded);
    }
  }, [liveUpdateCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const { quizData } = node.attrs;

  // Extract questions and title from quizData
  const questions = quizData?.questions || [];
  const title = quizData?.title || 'Interactive Quiz';
  const level = quizData?.level || 'B1';

  const isStudent = (() => {
    try {
      const root = editor?.view?.dom;
      const fromRoot = root?.getAttribute?.('data-student-mode');
      if (fromRoot) return fromRoot === 'true';
      const isStudentDashboard = typeof document !== 'undefined' &&
        (window.location.pathname.includes('/student-dashboard') ||
         window.location.pathname.includes('/student-profile'));
      return isStudentDashboard;
    } catch (_) { return false; }
  })();

  if (!quizData || !Array.isArray(questions)) {
    console.error('QuizEmbed - Invalid data:', { quizData, questions, title });
    return (
      <NodeViewWrapper>
        <div className="quiz-embed-error">
          <p>Error: Invalid Quiz data</p>
        </div>
      </NodeViewWrapper>
    );
  }

  const handleToggle = () => {
    const next = !isExpanded;
    setIsExpanded(next);
    updateState({ expanded: next });
  };

  const handleClose = () => {
    setIsExpanded(false);
    updateState({ expanded: false });
  };

  // Allow placing cursor above/below the embed (like Open Questions / ABCD)
  const insertParagraphAt = (pos) => {
    if (!editor || typeof pos !== 'number') return;
    editor
      .chain()
      .focus()
      .insertContentAt(pos, { type: 'paragraph' })
      .setTextSelection(pos + 1)
      .run();
  };

  const handleInsertAbove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const pos = typeof getPos === 'function' ? getPos() : null;
    insertParagraphAt(pos);
  };

  const handleInsertBelow = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const base = typeof getPos === 'function' ? getPos() : null;
    if (typeof base === 'number' && node && typeof node.nodeSize === 'number') {
      insertParagraphAt(base + node.nodeSize);
    }
  };

  const isHomework = (() => {
    try {
      const root = editor?.view?.dom;
      const fromRoot = root?.getAttribute?.('data-tab-type');
      if (fromRoot) return fromRoot === 'homework';
      const fromBody = typeof document !== 'undefined' ? document.body.getAttribute('data-novas-current-tab-type') : null;
      return fromBody === 'homework';
    } catch (_) { return false; }
  })();

  // Drag handlers for visual feedback
  const handleDragStart = (e) => {
    e.target.style.opacity = '0.5';
    e.target.style.transform = 'scale(0.95)';
    e.target.style.outline = 'none';
    e.target.style.border = 'none';
    e.target.style.boxShadow = 'none';
    e.target.classList.add('dragging');
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '';
    e.target.style.transform = '';
    e.target.style.outline = '';
    e.target.style.border = '';
    e.target.style.boxShadow = '';
    e.target.classList.remove('dragging');
  };

  return (
    <NodeViewWrapper className="quiz-embed" contentEditable={false} draggable={true} data-drag-handle onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="embed-insert-zone top" onMouseDown={handleInsertAbove} />
      <div className={`quiz-exercise-embed ${isExpanded ? 'expanded' : ''}`} draggable={true} data-drag-handle onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        {!isExpanded ? (
          <div className="unified-exercise-preview" draggable={true} data-drag-handle onClick={handleToggle} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <div className="quiz-preview-icon">
              <svg width="28" height="28" viewBox="0 0 512 512" fill="currentColor">
                <g>
                  <path d="m473.909 188.982h-121.034c14.132-19.61 22.47-43.66 22.47-69.624 0-65.814-53.544-119.358-119.36-119.358-65.814 0-119.358 53.544-119.358 119.358 0 25.964 8.338 50.014 22.47 69.624h-121.006c-21.003 0-38.091 17.088-38.091 38.091v192.355c0 21.003 17.088 38.091 38.091 38.091h164.723l45.716 51.145c1.897 2.122 4.608 3.335 7.454 3.336h.002c2.846 0 5.557-1.212 7.454-3.333l45.746-51.147h164.724c21.003 0 38.091-17.087 38.091-38.091v-192.356c-.001-21.003-17.089-38.091-38.092-38.091zm-217.924-168.982c54.787 0 99.359 44.572 99.359 99.358s-44.572 99.359-99.359 99.359c-54.786 0-99.358-44.572-99.358-99.359s44.572-99.358 99.358-99.358zm236.015 399.428c0 9.976-8.115 18.091-18.091 18.091h-169.196c-2.846 0-5.557 1.212-7.454 3.333l-41.271 46.145-41.244-46.142c-1.897-2.123-4.609-3.336-7.456-3.336h-169.197c-9.976 0-18.091-8.115-18.091-18.091v-192.355c0-9.975 8.115-18.091 18.091-18.091h139.157c21.03 18.498 48.594 29.735 78.737 29.735s57.708-11.237 78.738-29.735h139.186c9.976 0 18.091 8.116 18.091 18.091z"/>
                  <path d="m326.907 266.083c-5.522 0-10 4.477-10 10v96.404c0 5.523 4.478 10 10 10s10-4.477 10-10v-96.404c0-5.523-4.477-10-10-10z"/>
                  <path d="m446.641 362.487h-45.866l52.742-80.945c2.003-3.074 2.161-6.998.413-10.224s-5.123-5.235-8.792-5.235h-60.83c-5.522 0-10 4.477-10 10s4.478 10 10 10h42.379l-52.741 80.945c-2.004 3.074-2.162 6.998-.414 10.224s5.123 5.235 8.792 5.235h64.317c5.522 0 10-4.477 10-10s-4.478-10-10-10z"/>
                  <path d="m271.265 266.083c-5.522 0-10 4.477-10 10v66.273c0 4.791-1.313 11.822-12.688 17.554-3.376 1.71-7.216 2.577-11.373 2.577-4.683-.018-8.745-1.171-12.439-3.537-7.444-4.741-10.48-9.532-10.48-16.538v-66.33c0-5.523-4.478-10-10-10s-10 4.477-10 10v66.33c0 14.021 6.456 24.949 19.716 33.394 6.871 4.4 14.652 6.647 23.165 6.681 7.365 0 14.245-1.593 20.431-4.726 19.563-9.857 23.67-24.694 23.67-35.405v-66.273c-.002-5.523-4.48-10-10.002-10z"/>
                  <path d="m161.439 357.283c6.485-9.389 10.296-20.761 10.296-33.012 0-32.085-26.103-58.188-58.188-58.188-32.101 0-58.217 26.103-58.217 58.188 0 32.101 26.116 58.216 58.217 58.216 12.633 0 24.33-4.062 33.88-10.931l7.606 7.606c1.953 1.953 4.512 2.929 7.071 2.929s5.118-.977 7.071-2.929c3.905-3.905 3.905-10.237 0-14.142zm-47.891 5.204c-21.073 0-38.217-17.144-38.217-38.216 0-21.057 17.144-38.188 38.217-38.188 21.057 0 38.188 17.131 38.188 38.188 0 6.714-1.745 13.025-4.795 18.513l-8.605-8.605c-3.906-3.905-10.236-3.905-14.143 0-3.905 3.905-3.905 10.237 0 14.142l8.814 8.814c-5.703 3.395-12.356 5.352-19.459 5.352z"/>
                  <path d="m255.985 175.773c-5.522 0-10 4.477-10 10v.057c0 5.523 4.478 9.972 10 9.972s10-4.505 10-10.028-4.477-10.001-10-10.001z"/>
                  <path d="m225.655 93.217c5.522 0 10-4.477 10-10 0-1.043.061-1.972.188-2.861 1.429-10.182 10.271-17.675 20.551-17.5 10.613.229 19.357 8.691 19.907 19.265.293 5.641-1.682 10.997-5.56 15.082-3.885 4.092-9.125 6.345-14.756 6.345-5.522 0-10 4.477-10 10v31.719c0 5.523 4.478 10 10 10s10-4.477 10-10v-22.99c7.29-1.877 14.012-5.774 19.261-11.303 7.691-8.102 11.608-18.717 11.027-29.891-1.091-20.979-18.419-37.768-39.464-38.222-.273-.005-.545-.008-.816-.008-20.058 0-37.158 14.794-39.953 34.7-.259 1.812-.385 3.666-.385 5.665 0 5.522 4.478 9.999 10 9.999z"/>
                </g>
              </svg>
            </div>
            <div className="unified-preview-main" draggable={true} data-drag-handle>
              <div className="unified-preview-title">{title || 'Interactive Quiz'}</div>
              <div className="unified-preview-meta">
                {questions.length} questions
              </div>
            </div>
            <div className="unified-preview-actions" onClick={e => e.stopPropagation()}>
              <button
                className="unified-icon-btn"
                title="Add to homework"
                style={{ display: (isHomework || isStudent) ? 'none' : 'inline-flex' }}
                onClick={() => {
                  try {
                    window.dispatchEvent(new CustomEvent('novas:add-to-homework', {
                      detail: {
                        type: 'quiz',
                        attrs: { quizData: { questions, title } }
                      }
                    }));
                  } catch (_) {}
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="white" viewBox="0 0 16 16">
                  <path d="M8.707 1.5a1 1 0 0 0-1.414 0L.646 8.146a.5.5 0 0 0 .708.708L2 8.207V13.5A1.5 1.5 0 0 0 3.5 15h4a.5.5 0 1 0 0-1h-4a.5.5 0 0 1-.5-.5V7.207l5-5 6.646 6.647a.5.5 0 0 0 .708-.708L13 5.793V2.5a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5v1.293z"/>
                  <path d="M16 12.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0m-3.5-2a.5.5 0 0 0-.5.5v1h-1a.5.5 0 0 0 0 1h1v1a.5.5 0 1 0 1 0v-1h1a.5.5 0 1 0 0-1h-1v-1a.5.5 0 0 0-.5-.5"/>
                </svg>
              </button>
              <button
                className="unified-icon-btn"
                title="Delete"
                style={{ display: isStudent ? 'none' : 'inline-flex' }}
                onClick={() => editor?.chain().focus().deleteRange({ from: getPos(), to: getPos() + node.nodeSize }).run()}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="white" viewBox="0 0 16 16">
                  <path d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1zm3 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5M8 5a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7A.5.5 0 0 1 8 5m3 .5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 1 0"/>
                </svg>
              </button>
            </div>
          </div>
        ) : (
          <div className="embed-expanded" draggable={true} data-drag-handle onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <InteractiveQuiz
              questions={questions}
              title={title}
              level={level}
              onClose={handleClose}
              isStudent={isStudent}
              updateAttributes={(newData) => {
                updateAttributes({
                  quizData: {
                    ...quizData,
                    questions: newData.questions,
                    title: newData.title,
                    level: newData.level || level
                  }
                });
              }}
              node={node}
              editor={editor}
              getPos={getPos}
              myState={myState}
              otherState={otherState}
              updateState={updateState}
            />
          </div>
        )}
      </div>
      <div className="embed-insert-zone bottom" onMouseDown={handleInsertBelow} />
    </NodeViewWrapper>
  );
};

export default QuizEmbed; 